import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { ArrowLeft, X, Loader2, Save, ImagePlus, Star, EyeOff } from 'lucide-react';
import PlateBlurModal from '../../components/Cars/PlateBlurModal';
import { cldImg, compressImage } from '../../lib/utils';
import PageHeader from '../../components/layout/PageHeader';
import Card, { CardContent } from '../../components/ui/Card';
import Button from '../../components/ui/Button';
import Input from '../../components/ui/Input';
import Select from '../../components/ui/Select';
import CarLoader from '../../components/ui/CarLoader';
import { ToastModal } from '../../components/ui/Toast';
import ConfirmModal from '../../components/ui/ConfirmModal';
import { carAPI } from '../../services/api';
import AddressAutocomplete from '../../components/maps/AddressAutocomplete';
import { useVehicleTypes } from '../../hooks/useVehicleTypes';
import { purposesFor, purposeFromCar, purposeTitle, OWNER_OPTIONS } from '../../lib/vehiclePurpose';
import { useAuth } from '../../context/AuthContext';

// Convert number to Indian words (lakhs, crores)
const numberToIndianWords = (num) => {
  if (!num || isNaN(num) || num <= 0) return '';

  const ones = ['', 'One', 'Two', 'Three', 'Four', 'Five', 'Six', 'Seven', 'Eight', 'Nine', 'Ten',
    'Eleven', 'Twelve', 'Thirteen', 'Fourteen', 'Fifteen', 'Sixteen', 'Seventeen', 'Eighteen', 'Nineteen'];
  const tens = ['', '', 'Twenty', 'Thirty', 'Forty', 'Fifty', 'Sixty', 'Seventy', 'Eighty', 'Ninety'];

  const numToWords = (n) => {
    if (n < 20) return ones[n];
    if (n < 100) return tens[Math.floor(n / 10)] + (n % 10 ? ' ' + ones[n % 10] : '');
    if (n < 1000) return ones[Math.floor(n / 100)] + ' Hundred' + (n % 100 ? ' ' + numToWords(n % 100) : '');
    if (n < 100000) return numToWords(Math.floor(n / 1000)) + ' Thousand' + (n % 1000 ? ' ' + numToWords(n % 1000) : '');
    if (n < 10000000) return numToWords(Math.floor(n / 100000)) + ' Lakh' + (n % 100000 ? ' ' + numToWords(n % 100000) : '');
    return numToWords(Math.floor(n / 10000000)) + ' Crore' + (n % 10000000 ? ' ' + numToWords(n % 10000000) : '');
  };

  return numToWords(Math.floor(num)) + ' Rupees Only';
};

const SectionTitle = ({ children }) => (
  <div className="border-b border-gray-200 pb-2 mb-4">
    <h3 className="text-sm font-semibold text-gray-800 uppercase tracking-wide">{children}</h3>
  </div>
);

const EditCar = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  // Live vehicle types; keep the emoji-prefixed label format this page used before.
  const { vehicleTypes: rawVehicleTypes } = useVehicleTypes();
  const VEHICLE_TYPES = rawVehicleTypes.map(t => ({ ...t, label: `${t.icon} ${t.label}` }));
  const fileInputRef = useRef(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [uploadingImages, setUploadingImages] = useState(false);
  // Read the vehicle's current purpose; the owner can switch it while editing —
  // but only between the purposes their business actually offers. AddCar already
  // works this way; Edit offered all three to everyone, so a sell-only shop could
  // put a vehicle back into the rental listings.
  const { user } = useAuth();
  const purposeOptions = purposesFor(user?.businessType || 'both');
  const [purpose, setPurpose] = useState('rental');
  const [changingPurpose, setChangingPurpose] = useState(false);
  const forRent = purpose !== 'sale';
  const forSale = purpose !== 'rental';
  const [formData, setFormData] = useState({
    vehicleType: 'car',
    vehicleCategory: 'four-wheeler',
    make: '',
    model: '',
    variant: '',
    year: new Date().getFullYear(),
    registrationNumber: '',
    seats: 5,
    fuelType: 'Petrol',
    transmission: 'Manual',
    registrationBoard: 'white',
    permitType: '',
    permitValidTill: '',
    fitnessValidTill: '',
    commercialInsurance: false,
    pricePerDay: '',
    pricePerKm: '',
    city: '',
    latitude: null,
    longitude: null,
    state: '',
    isAvailable: true,
    color: '',
    mileage: '',
    features: '',
    description: '',
    serviceTypes: ['rental'],
    forSale: false,
    salePrice: '',
    salePriceType: 'negotiable',
    saleKmDriven: '',
    saleNoOfOwners: '',
    saleCondition: '',
    saleInsuranceValidTill: '',
    saleRegistrationYear: '',
    saleAccidentFree: true,
    saleRcAvailable: true,
    saleLoanStatus: '',
    minBookingDays: 1,
    outstationPricePerKm: '',
    driverBataPerDay: '',
    cargoCapacity: '',
    cargoUnit: 'kg',
    helmetIncluded: false,
    engineCC: '',
    acAvailable: false,
    sleeper: false,
    pushback: false,
    withOperator: false,
    operatorChargePerDay: ''
  });

  const [existingImages, setExistingImages] = useState([]);
  const [selectedFiles, setSelectedFiles] = useState([]);
  const [imagePreviews, setImagePreviews] = useState([]);
  const [errors, setErrors] = useState({});
  const [toast, setToast] = useState({ isOpen: false, title: '', message: '', type: 'success' });
  const [confirm, setConfirm] = useState({ isOpen: false, title: '', message: '', type: 'danger', confirmText: 'Delete', onConfirm: null });

  useEffect(() => {
    fetchCar();
  }, [id]); // eslint-disable-line react-hooks/exhaustive-deps

  const fetchCar = async () => {
    try {
      setLoading(true);
      const response = await carAPI.getCarById(id);
      const car = response.data.car || response.data;

      setPurpose(purposeFromCar(car));

      setFormData({
        vehicleType: car.vehicleType || 'car',
        vehicleCategory: car.vehicleCategory || 'four-wheeler',
        make: car.make || '',
        model: car.model || '',
        variant: car.variant || '',
        year: car.year || new Date().getFullYear(),
        registrationNumber: car.registrationNumber || '',
        seats: car.seats || 5,
        fuelType: car.fuelType || 'Petrol',
        transmission: car.transmission || 'Manual',
        registrationBoard: car.registrationBoard || 'white',
        permitType: car.commercialDetails?.permitType || '',
        permitValidTill: car.commercialDetails?.permitValidTill ? car.commercialDetails.permitValidTill.split('T')[0] : '',
        fitnessValidTill: car.commercialDetails?.fitnessValidTill ? car.commercialDetails.fitnessValidTill.split('T')[0] : '',
        commercialInsurance: car.commercialDetails?.commercialInsurance || false,
        pricePerDay: car.pricePerDay || '',
        pricePerKm: car.pricePerKm || '',
        city: car.location?.city || '',
        state: car.location?.state || '',
        latitude: car.location?.coordinates?.latitude || null,
        longitude: car.location?.coordinates?.longitude || null,
        isAvailable: car.isAvailable !== false,
        color: car.color || '',
        mileage: car.mileage || '',
        features: car.features?.join(', ') || '',
        description: car.description || '',
        serviceTypes: car.serviceTypes || ['rental'],
        forSale: car.forSale || false,
        salePrice: car.salePrice || '',
        salePriceType: car.salePriceType || 'negotiable',
        saleKmDriven: car.saleDetails?.kmDriven ?? '',
        saleNoOfOwners: car.saleDetails?.noOfOwners ?? '',
        saleCondition: car.saleDetails?.condition || '',
        saleInsuranceValidTill: car.saleDetails?.insuranceValidTill ? car.saleDetails.insuranceValidTill.split('T')[0] : '',
        saleRegistrationYear: car.saleDetails?.registrationYear ?? '',
        saleAccidentFree: car.saleDetails?.accidentFree !== false,
        saleRcAvailable: car.saleDetails?.rcAvailable !== false,
        saleLoanStatus: car.saleDetails?.loanStatus || '',
        minBookingDays: car.minBookingDays || 1,
        outstationPricePerKm: car.outstationPricePerKm || '',
        driverBataPerDay: car.driverBataPerDay || '',
        cargoCapacity: car.vehicleDetails?.cargoCapacity || '',
        cargoUnit: car.vehicleDetails?.cargoUnit || 'kg',
        helmetIncluded: car.vehicleDetails?.helmetIncluded || false,
        engineCC: car.vehicleDetails?.engineCC || '',
        acAvailable: car.vehicleDetails?.acAvailable || false,
        sleeper: car.vehicleDetails?.sleeper || false,
        pushback: car.vehicleDetails?.pushback || false,
        withOperator: car.vehicleDetails?.withOperator || false,
        operatorChargePerDay: car.vehicleDetails?.operatorChargePerDay || ''
      });

      const validImages = (car.images || []).filter(img => img && img.trim && img.trim() !== '');
      setExistingImages(validImages);
    } catch (error) {
      console.error('Error fetching car:', error);
      setToast({
        isOpen: true,
        title: 'Error',
        message: `Failed to load car: ${error.response?.data?.message || error.message}`,
        type: 'error'
      });
      setTimeout(() => navigate('/cars'), 2000);
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
    if (errors[name]) {
      setErrors(prev => ({ ...prev, [name]: '' }));
    }
  };

  const handleVehicleTypeChange = (type) => {
    const selectedType = VEHICLE_TYPES.find(v => v.value === type);
    setFormData(prev => ({
      ...prev,
      vehicleType: type,
      vehicleCategory: selectedType?.category || 'four-wheeler',
      seats: selectedType?.seats || 4
    }));
  };

  const handleFileSelect = async (e) => {
    const picked = Array.from(e.target.files);
    const totalImages = existingImages.length + selectedFiles.length + picked.length;

    if (totalImages > 20) {
      setToast({ isOpen: true, title: 'Too Many Images', message: 'Add up to 20 images per upload (you can add more anytime).', type: 'warning' });
      return;
    }

    // Compress/resize in the browser before upload so it's fast.
    const files = await Promise.all(picked.map(f => compressImage(f)));
    const newPreviews = files.map(file => ({
      file,
      preview: URL.createObjectURL(file),
      name: file.name
    }));

    setSelectedFiles(prev => [...prev, ...files]);
    setImagePreviews(prev => [...prev, ...newPreviews]);
  };

  const handleDrop = async (e) => {
    e.preventDefault();
    const picked = Array.from(e.dataTransfer.files).filter(file => file.type.startsWith('image/'));
    const totalImages = existingImages.length + selectedFiles.length + picked.length;
    if (totalImages > 20) {
      setToast({ isOpen: true, title: 'Too Many Images', message: 'Add up to 20 images per upload (you can add more anytime).', type: 'warning' });
      return;
    }

    const files = await Promise.all(picked.map(f => compressImage(f)));
    const newPreviews = files.map(file => ({
      file,
      preview: URL.createObjectURL(file),
      name: file.name
    }));

    setSelectedFiles(prev => [...prev, ...files]);
    setImagePreviews(prev => [...prev, ...newPreviews]);
  };

  const handleDragOver = (e) => e.preventDefault();


  // Pixelating a plate happens on the pending File, before upload — an already
  // uploaded photo has to be removed and re-added instead.
  const [blurIndex, setBlurIndex] = useState(null);
  // Same editor, pointed at a photo that is already on Cloudinary. The edited
  // copy is uploaded into the old one's slot and the unmasked original is
  // destroyed server-side, so masking a cover photo does not demote it.
  const [blurExistingUrl, setBlurExistingUrl] = useState(null);
  const replaceExisting = async (oldUrl, blurredFile) => {
    const form = new FormData();
    form.append('image', blurredFile);
    form.append('oldImageUrl', oldUrl);
    const { data } = await carAPI.replaceImage(id, form);
    if (!data?.success) throw new Error(data?.message || 'Replace failed');
    setExistingImages(prev => prev.map(u => (u === oldUrl ? data.imageUrl : u)));
    setBlurExistingUrl(null);
    setToast({ isOpen: true, title: 'Photo updated', message: 'The edited photo replaced the original.', type: 'success' });
  };
  const applyBlur = (index, blurredFile) => {
    setSelectedFiles(prev => prev.map((f, i) => (i === index ? blurredFile : f)));
    setImagePreviews(prev => prev.map((p, i) => {
      if (i !== index) return p;
      URL.revokeObjectURL(p.preview);
      return { file: blurredFile, preview: URL.createObjectURL(blurredFile), name: blurredFile.name };
    }));
    setBlurIndex(null);
  };

  const removeNewImage = (index) => {
    URL.revokeObjectURL(imagePreviews[index].preview);
    setSelectedFiles(prev => prev.filter((_, i) => i !== index));
    setImagePreviews(prev => prev.filter((_, i) => i !== index));
  };

  // The cover shown across the app is images[0], so "set cover" just moves it to the front.
  const setAsCover = async (imageUrl) => {
    if (!imageUrl || existingImages[0] === imageUrl) return;
    const previous = existingImages;
    const reordered = [imageUrl, ...existingImages.filter(img => img !== imageUrl)];
    setExistingImages(reordered);
    try {
      await carAPI.updateCar(id, { images: reordered });
      setToast({ isOpen: true, title: 'Cover Updated', message: 'This photo is now the cover.', type: 'success' });
    } catch (error) {
      setExistingImages(previous);
      setToast({
        isOpen: true,
        title: 'Error',
        message: 'Could not set the cover photo: ' + (error.response?.data?.message || error.message),
        type: 'error'
      });
    }
  };

  const removeExistingImage = async (imageUrl) => {
    if (!imageUrl) return;
    try {
      await carAPI.deleteImage(id, imageUrl);
      setExistingImages(prev => prev.filter(img => img !== imageUrl));
      setToast({ isOpen: true, title: 'Image Deleted', message: 'Image removed successfully.', type: 'success' });
    } catch (error) {
      setToast({
        isOpen: true,
        title: 'Error',
        message: 'Failed to delete image: ' + (error.response?.data?.message || error.message),
        type: 'error'
      });
    }
  };

  const validateForm = () => {
    const newErrors = {};
    if (!formData.make) newErrors.make = 'Required';
    if (!formData.model) newErrors.model = 'Required';
    if (!formData.year) newErrors.year = 'Required';
    if (!formData.registrationNumber) newErrors.registrationNumber = 'Required';
    if (!formData.seats) newErrors.seats = 'Required';
    if (!formData.city) newErrors.city = 'Required';
    // Only ask for the price that matches what this vehicle is listed for.
    if (forRent && !formData.pricePerDay) newErrors.pricePerDay = 'Required';
    if (forSale && (!formData.salePrice || parseFloat(formData.salePrice) <= 0)) {
      newErrors.salePrice = 'Sale price required';
    }
    // A vehicle cannot be registered before it was manufactured.
    if (forSale && formData.saleRegistrationYear && formData.year
        && Number(formData.saleRegistrationYear) < Number(formData.year)) {
      newErrors.saleRegistrationYear = `Cannot be earlier than the manufacturing year (${formData.year})`;
    }
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validateForm()) return;

    try {
      setSaving(true);

      const carData = {
        vehicleType: formData.vehicleType,
        vehicleCategory: formData.vehicleCategory,
        make: formData.make,
        model: formData.model,
        variant: formData.variant?.trim() || '',
        year: parseInt(formData.year),
        registrationNumber: formData.registrationNumber.toUpperCase(),
        seats: parseInt(formData.seats),
        fuelType: formData.fuelType,
        transmission: formData.transmission,
        registrationBoard: formData.registrationBoard,
        commercialDetails: (forRent && ['yellow', 'black'].includes(formData.registrationBoard)) ? {
          permitType: formData.permitType || '',
          permitValidTill: formData.permitValidTill || undefined,
          fitnessValidTill: formData.fitnessValidTill || undefined,
          commercialInsurance: !!formData.commercialInsurance
        } : undefined,
        pricePerDay: parseFloat(formData.pricePerDay) || 0,
        pricePerKm: parseFloat(formData.pricePerKm) || 0,
        color: formData.color,
        mileage: parseFloat(formData.mileage) || 0,
        description: formData.description,
        features: formData.features ? formData.features.split(',').map(f => f.trim()).filter(f => f) : [],
        isAvailable: formData.isAvailable,
        location: {
          city: formData.city,
          state: formData.state,
          coordinates: {
            latitude: formData.latitude,
            longitude: formData.longitude
          }
        },
        serviceTypes: forRent ? formData.serviceTypes : [],
        forRent,
        forSale,
        salePrice: parseFloat(formData.salePrice) || 0,
        salePriceType: formData.salePriceType,
        saleDetails: forSale ? {
          kmDriven: formData.saleKmDriven ? parseInt(formData.saleKmDriven) : undefined,
          noOfOwners: formData.saleNoOfOwners ? parseInt(formData.saleNoOfOwners) : undefined,
          condition: formData.saleCondition || undefined,
          insuranceValidTill: formData.saleInsuranceValidTill || undefined,
          registrationYear: formData.saleRegistrationYear ? parseInt(formData.saleRegistrationYear) : undefined,
          accidentFree: formData.saleAccidentFree,
          rcAvailable: formData.saleRcAvailable,
          loanStatus: formData.saleLoanStatus || ''
        } : undefined,
        minBookingDays: parseInt(formData.minBookingDays) || 1,
        outstationPricePerKm: parseFloat(formData.outstationPricePerKm) || 0,
        driverBataPerDay: parseFloat(formData.driverBataPerDay) || 0,
        vehicleDetails: {
          cargoCapacity: parseFloat(formData.cargoCapacity) || 0,
          cargoUnit: formData.cargoUnit,
          helmetIncluded: formData.helmetIncluded,
          engineCC: parseFloat(formData.engineCC) || 0,
          acAvailable: formData.acAvailable,
          sleeper: formData.sleeper,
          pushback: formData.pushback,
          withOperator: formData.withOperator,
          operatorChargePerDay: parseFloat(formData.operatorChargePerDay) || 0
        }
      };

      await carAPI.updateCar(id, carData);

      if (selectedFiles.length > 0) {
        setUploadingImages(true);
        const formDataImages = new FormData();
        selectedFiles.forEach(file => formDataImages.append('images', file));
        try {
          await carAPI.uploadImages(id, formDataImages);
        } catch (uploadError) {
          console.error('Error uploading images:', uploadError);
        }
        setUploadingImages(false);
      }

      setToast({ isOpen: true, title: 'Vehicle Updated!', message: 'Vehicle details updated successfully.', type: 'success' });
      setTimeout(() => navigate(`/cars/${id}`), 1500);
    } catch (error) {
      console.error('Error updating car:', error);
      setToast({
        isOpen: true,
        title: 'Error',
        message: error.response?.data?.message || 'Failed to update car',
        type: 'error'
      });
    } finally {
      setSaving(false);
    }
  };

  const isTwoWheeler = formData.vehicleType === 'bike' || formData.vehicleType === 'scooter';
  const isCommercial = formData.vehicleType === 'truck' || formData.vehicleType === 'van' || formData.vehicleType === 'pickup';
  const isBus = formData.vehicleType === 'bus' || formData.vehicleType === 'tempo-traveller';
  const isSpecial = formData.vehicleType === 'jcb' || formData.vehicleType === 'tractor';
  const hasOutstation = formData.serviceTypes.includes('outstation') || formData.serviceTypes.includes('km-based');

  if (loading) {
    return (
      <div className="py-8">
        <CarLoader message="Loading car data..." />
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <PageHeader
        title="Edit Vehicle"
        description={`${formData.make} ${formData.model} - ${formData.registrationNumber}`}
        action={
          <Button variant="outline" onClick={() => navigate('/cars')}>
            <ArrowLeft className="h-4 w-4 mr-1" />
            Back
          </Button>
        }
      />

      {/* Purpose — what this vehicle is listed for; drives which fields are shown */}
      <div className="rounded-xl border border-blue-100 bg-blue-50 px-4 py-3">
        <div className="flex items-center justify-between gap-3">
          <span className="text-sm text-gray-700">
            Listed for <span className="font-semibold text-gray-900">{purposeTitle(purpose)}</span>
          </span>
          {purposeOptions.length > 1 && (
            <button
              type="button"
              onClick={() => setChangingPurpose(v => !v)}
              className="text-sm font-semibold text-blue-600 hover:text-blue-700 underline underline-offset-2"
            >
              {changingPurpose ? 'Cancel' : 'Change'}
            </button>
          )}
        </div>

        {changingPurpose && (
          <div className="grid grid-cols-3 gap-2 mt-3">
            {purposeOptions.map(({ value, title, icon: Icon }) => (
              <button
                key={value}
                type="button"
                onClick={() => { setPurpose(value); setChangingPurpose(false); }}
                className={`flex flex-col items-center gap-1 rounded-lg border-2 bg-white px-3 py-2.5 text-xs font-semibold transition-colors ${
                  purpose === value
                    ? 'border-blue-600 text-blue-700'
                    : 'border-gray-200 text-gray-700 hover:border-blue-400'
                }`}
              >
                <Icon className="h-4 w-4" />
                {title}
              </button>
            ))}
          </div>
        )}
      </div>

      <form onSubmit={handleSubmit}>
        {/* Vehicle Photos — responsive gallery grid */}
        <Card className="mb-4">
          <CardContent className="p-5">
            <div className="flex items-end justify-between mb-4">
              <div>
                <h3 className="text-base font-semibold text-gray-900">Vehicle Photos</h3>
                <p className="text-xs text-gray-500 mt-0.5">
                  The first photo is the cover. Up to 20 photos · JPG, PNG, WebP, HEIC.
                </p>
                {/* The API no longer publishes the registration number, but a plate
                    readable in a photo puts it back — and a sale listing pairs it
                    with a price and a location. Only worth saying where it matters. */}
                {forSale && (
                  <p className="text-xs text-amber-700 mt-1">
                    Tip: avoid photos where the number plate is readable — buyers don't need it,
                    and it can be used to look up your details.
                  </p>
                )}
              </div>
              <span className="text-xs font-medium text-gray-600 bg-gray-100 px-2.5 py-1 rounded-full whitespace-nowrap">
                {existingImages.length + imagePreviews.length}/20
              </span>
            </div>

            <div className="max-h-[56vh] overflow-y-auto pr-1 -mr-1">
            <div className="grid grid-cols-3 sm:grid-cols-4 md:grid-cols-5 xl:grid-cols-6 gap-2.5">
              {/* Existing images */}
              {existingImages.map((img, index) => (
                <div
                  key={`existing-${index}`}
                  className="group relative aspect-[4/3] rounded-xl overflow-hidden border border-gray-200 bg-gray-100 shadow-sm transition-all duration-200 hover:shadow-md hover:scale-[1.02]"
                >
                  <img
                    src={cldImg(img, { w: 400 })}
                    alt={`Vehicle photo ${index + 1}`}
                    loading="lazy"
                    ref={(el) => { if (el && el.complete) el.classList.remove('opacity-0'); }}
                    onLoad={(e) => e.currentTarget.classList.remove('opacity-0')}
                    className="w-full h-full object-cover opacity-0 transition-opacity duration-300"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/45 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity" />
                  {index === 0 && (
                    <span className="absolute top-2 left-2 inline-flex items-center gap-1 bg-white/90 backdrop-blur-sm text-gray-800 text-[11px] font-semibold px-2 py-1 rounded-full shadow-sm">
                      <Star className="h-3 w-3 text-amber-500 fill-amber-500" /> Cover
                    </span>
                  )}
                  {index !== 0 && (
                    <button
                      type="button"
                      onClick={(e) => {
                        e.preventDefault();
                        e.stopPropagation();
                        setAsCover(img);
                      }}
                      className="absolute bottom-1.5 left-1.5 inline-flex items-center gap-1 bg-white/90 text-gray-700 hover:bg-amber-500 hover:text-white rounded-full px-2 py-1 text-[11px] font-semibold shadow-md opacity-100 sm:opacity-0 sm:group-hover:opacity-100 transition-all"
                      title="Make this the cover photo"
                    >
                      <Star className="h-3 w-3" /> Set cover
                    </button>
                  )}
                  <button
                    type="button"
                    onClick={(e) => { e.preventDefault(); e.stopPropagation(); setBlurExistingUrl(img); }}
                    className="absolute bottom-1.5 right-1.5 inline-flex items-center gap-1 bg-white/90 text-gray-700 hover:bg-gray-900 hover:text-white rounded-full px-2 py-1 text-[11px] font-semibold shadow-md opacity-100 sm:opacity-0 sm:group-hover:opacity-100 transition-all"
                    title="Pixelate the number plate in this photo"
                  >
                    <EyeOff className="h-3 w-3" /> Hide plate
                  </button>
                  <button
                    type="button"
                    onClick={(e) => {
                      e.preventDefault();
                      e.stopPropagation();
                      setConfirm({
                        isOpen: true,
                        title: 'Delete Image?',
                        message: 'Are you sure you want to delete this image? This cannot be undone.',
                        type: 'danger',
                        confirmText: 'Delete',
                        onConfirm: () => removeExistingImage(img)
                      });
                    }}
                    className="absolute top-1.5 right-1.5 bg-white/90 text-red-600 hover:bg-red-600 hover:text-white rounded-full p-1.5 shadow-md opacity-90 hover:opacity-100 transition-all"
                    title="Delete photo"
                  >
                    <X className="h-4 w-4" strokeWidth={2.5} />
                  </button>
                </div>
              ))}

              {/* Newly selected (not yet uploaded) images */}
              {imagePreviews.map((img, index) => (
                <div
                  key={`new-${index}`}
                  className="group relative aspect-[4/3] rounded-xl overflow-hidden border-2 border-green-400 bg-gray-100 shadow-sm transition-all duration-200 hover:shadow-md hover:scale-[1.02]"
                >
                  <img
                    src={img.preview}
                    alt={`New photo ${index + 1}`}
                    loading="lazy"
                    ref={(el) => { if (el && el.complete) el.classList.remove('opacity-0'); }}
                    onLoad={(e) => e.currentTarget.classList.remove('opacity-0')}
                    className="w-full h-full object-cover opacity-0 transition-opacity duration-300"
                  />
                  <span className="absolute top-2 left-2 bg-green-500 text-white text-[11px] font-semibold px-2 py-0.5 rounded-full shadow-sm">
                    New
                  </span>
                  <button
                    type="button"
                    onClick={() => {
                      setConfirm({
                        isOpen: true,
                        title: 'Remove Image?',
                        message: 'Are you sure you want to remove this image?',
                        type: 'danger',
                        confirmText: 'Remove',
                        onConfirm: () => removeNewImage(index)
                      });
                    }}
                    className="absolute top-1.5 right-1.5 bg-white/90 text-red-600 hover:bg-red-600 hover:text-white rounded-full p-1.5 shadow-md opacity-90 hover:opacity-100 transition-all"
                    title="Remove photo"
                  >
                    <X className="h-4 w-4" strokeWidth={2.5} />
                  </button>
                  <button
                    type="button"
                    onClick={() => setBlurIndex(index)}
                    className="absolute bottom-1.5 right-1.5 flex items-center gap-1 bg-white/90 text-gray-700 hover:bg-gray-900 hover:text-white rounded-full px-2 py-1 text-[11px] font-semibold shadow-md transition-all"
                    title="Pixelate the number plate before uploading"
                  >
                    <EyeOff className="h-3.5 w-3.5" strokeWidth={2.5} />
                    Hide plate
                  </button>
                </div>
              ))}

              {/* Add-photo tile (drag & drop or click) */}
              {(existingImages.length + imagePreviews.length) < 20 && (
                <div
                  onDrop={handleDrop}
                  onDragOver={handleDragOver}
                  onClick={() => fileInputRef.current?.click()}
                  className="aspect-[4/3] rounded-xl border-2 border-dashed border-gray-300 bg-gray-50/60 flex flex-col items-center justify-center cursor-pointer text-gray-400 hover:border-blue-400 hover:text-blue-500 hover:bg-blue-50/50 transition-colors"
                >
                  <ImagePlus className="h-6 w-6 mb-1" />
                  <span className="text-xs font-medium">Add Photo</span>
                  <span className="hidden md:block text-[10px] text-gray-400 mt-0.5">drag &amp; drop</span>
                  <input
                    ref={fileInputRef}
                    type="file"
                    accept="image/*,.heic,.heif"
                    multiple
                    onChange={handleFileSelect}
                    className="hidden"
                  />
                </div>
              )}
            </div>
            </div>

            {uploadingImages && (
              <div className="flex items-center gap-2 text-sm text-blue-600 mt-4">
                <Loader2 className="h-4 w-4 animate-spin" /> Uploading photos…
              </div>
            )}
          </CardContent>
        </Card>

        <Card>
          <CardContent className="p-5">
            {/* Vehicle Info */}
            <SectionTitle>Vehicle Information</SectionTitle>
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4 mb-6">
              <Select
                label="Vehicle Type"
                name="vehicleType"
                value={formData.vehicleType}
                onChange={(e) => handleVehicleTypeChange(e.target.value)}
              >
                {VEHICLE_TYPES.map((type) => (
                  <option key={type.value} value={type.value}>{type.label}</option>
                ))}
              </Select>
              <Input
                label={isTwoWheeler ? 'Brand' : 'Make'}
                name="make"
                value={formData.make}
                onChange={handleChange}
                error={errors.make}
                required
                placeholder="e.g., Maruti"
              />
              <Input
                label="Model"
                name="model"
                value={formData.model}
                onChange={handleChange}
                error={errors.model}
                required
                placeholder="e.g., Ertiga"
              />
              <Input
                label="Variant"
                name="variant"
                value={formData.variant}
                onChange={handleChange}
                placeholder="e.g., ZXI Plus AT"
              />
              <Input
                label="Year"
                name="year"
                type="number"
                value={formData.year}
                onChange={handleChange}
                error={errors.year}
                required
              />
              <Input
                label="Registration No."
                name="registrationNumber"
                value={formData.registrationNumber}
                onChange={handleChange}
                error={errors.registrationNumber}
                required
                placeholder="e.g., KA-26 M6048"
              />
              <Select
                label="Registration Board"
                name="registrationBoard"
                value={formData.registrationBoard}
                onChange={handleChange}
              >
                <option value="white">White — Private</option>
                <option value="yellow">Yellow — Commercial</option>
                <option value="green">Green — Electric (EV)</option>
                <option value="black">Black — Self-drive Rental</option>
              </Select>
              {forRent && ['yellow', 'black'].includes(formData.registrationBoard) && (
                <>
                  <Select
                    label="Permit Type"
                    name="permitType"
                    value={formData.permitType}
                    onChange={handleChange}
                  >
                    <option value="">Select permit</option>
                    <option value="state">State</option>
                    <option value="national">National</option>
                    <option value="all-india">All-India</option>
                    <option value="contract-carriage">Contract Carriage</option>
                  </Select>
                  <Input
                    label="Permit Valid Till"
                    name="permitValidTill"
                    type="date"
                    value={formData.permitValidTill}
                    onChange={handleChange}
                  />
                  <Input
                    label="Fitness Valid Till"
                    name="fitnessValidTill"
                    type="date"
                    value={formData.fitnessValidTill}
                    onChange={handleChange}
                  />
                  <Select
                    label="Commercial Insurance"
                    name="commercialInsurance"
                    value={formData.commercialInsurance ? 'yes' : 'no'}
                    onChange={(e) => setFormData(prev => ({ ...prev, commercialInsurance: e.target.value === 'yes' }))}
                  >
                    <option value="no">No</option>
                    <option value="yes">Yes</option>
                  </Select>
                </>
              )}
              <Input
                label="Color"
                name="color"
                value={formData.color}
                onChange={handleChange}
                placeholder="e.g., White"
              />
              <Input
                label="Mileage (km/l)"
                name="mileage"
                type="number"
                value={formData.mileage}
                onChange={handleChange}
              />
              <Input
                label="Seating Capacity"
                name="seats"
                type="number"
                value={formData.seats}
                onChange={handleChange}
                error={errors.seats}
                required
                min="1"
                max={isBus ? '60' : formData.vehicleType === 'tempo-traveller' ? '26' : '20'}
              />
            </div>

            {/* Specifications */}
            <SectionTitle>Specifications</SectionTitle>
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4 mb-6">
              <Select
                label="Fuel Type"
                name="fuelType"
                value={formData.fuelType}
                onChange={handleChange}
              >
                <option value="Petrol">Petrol</option>
                <option value="Diesel">Diesel</option>
                <option value="Electric">Electric</option>
                <option value="Hybrid">Hybrid</option>
                <option value="CNG">CNG</option>
              </Select>
              <Select
                label="Transmission"
                name="transmission"
                value={formData.transmission}
                onChange={handleChange}
              >
                <option value="Manual">Manual</option>
                <option value="Automatic">Automatic</option>
              </Select>
              <Select
                label="Availability"
                name="isAvailable"
                value={formData.isAvailable}
                onChange={(e) => setFormData(prev => ({ ...prev, isAvailable: e.target.value === 'true' }))}
              >
                <option value="true">Available</option>
                <option value="false">Not Available</option>
              </Select>

              {/* Two-Wheeler specific */}
              {isTwoWheeler && (
                <>
                  <Input
                    label="Engine CC"
                    name="engineCC"
                    type="number"
                    value={formData.engineCC}
                    onChange={handleChange}
                    placeholder="e.g., 150"
                  />
                </>
              )}

              {/* Commercial specific */}
              {isCommercial && (
                <>
                  <Input
                    label="Cargo Capacity"
                    name="cargoCapacity"
                    type="number"
                    value={formData.cargoCapacity}
                    onChange={handleChange}
                    placeholder="e.g., 1000"
                  />
                  <Select
                    label="Capacity Unit"
                    name="cargoUnit"
                    value={formData.cargoUnit}
                    onChange={handleChange}
                  >
                    <option value="kg">Kilograms (kg)</option>
                    <option value="ton">Tons</option>
                  </Select>
                </>
              )}

              {/* Special vehicle specific */}
              {isSpecial && formData.withOperator && (
                <Input
                  label="Operator Charge/Day (₹)"
                  name="operatorChargePerDay"
                  type="number"
                  value={formData.operatorChargePerDay}
                  onChange={handleChange}
                  placeholder="e.g., 800"
                />
              )}
            </div>

            {/* Checkbox options for specific vehicle types */}
            {(isTwoWheeler || isBus || isSpecial) && (
              <div className="flex flex-wrap gap-4 mb-6">
                {isTwoWheeler && (
                  <label className="flex items-center space-x-2 text-sm cursor-pointer">
                    <input
                      type="checkbox"
                      checked={formData.helmetIncluded}
                      onChange={(e) => setFormData(prev => ({ ...prev, helmetIncluded: e.target.checked }))}
                      className="w-4 h-4 text-blue-600 rounded"
                    />
                    <span className="text-gray-700">Helmet Included</span>
                  </label>
                )}
                {isBus && (
                  <>
                    <label className="flex items-center space-x-2 text-sm cursor-pointer">
                      <input
                        type="checkbox"
                        checked={formData.acAvailable}
                        onChange={(e) => setFormData(prev => ({ ...prev, acAvailable: e.target.checked }))}
                        className="w-4 h-4 text-blue-600 rounded"
                      />
                      <span className="text-gray-700">AC Available</span>
                    </label>
                    <label className="flex items-center space-x-2 text-sm cursor-pointer">
                      <input
                        type="checkbox"
                        checked={formData.sleeper}
                        onChange={(e) => setFormData(prev => ({ ...prev, sleeper: e.target.checked }))}
                        className="w-4 h-4 text-blue-600 rounded"
                      />
                      <span className="text-gray-700">Sleeper</span>
                    </label>
                    <label className="flex items-center space-x-2 text-sm cursor-pointer">
                      <input
                        type="checkbox"
                        checked={formData.pushback}
                        onChange={(e) => setFormData(prev => ({ ...prev, pushback: e.target.checked }))}
                        className="w-4 h-4 text-blue-600 rounded"
                      />
                      <span className="text-gray-700">Pushback Seats</span>
                    </label>
                  </>
                )}
                {isSpecial && (
                  <label className="flex items-center space-x-2 text-sm cursor-pointer">
                    <input
                      type="checkbox"
                      checked={formData.withOperator}
                      onChange={(e) => setFormData(prev => ({ ...prev, withOperator: e.target.checked }))}
                      className="w-4 h-4 text-blue-600 rounded"
                    />
                    <span className="text-gray-700">Comes with Operator</span>
                  </label>
                )}
              </div>
            )}

            {/* Location & Pricing */}
            <SectionTitle>Location & Pricing</SectionTitle>
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4 mb-6">
              <div className="col-span-2">
                <AddressAutocomplete
                  label="Search Location"
                  placeholder="Search vehicle location..."
                  value={formData.city}
                  onChange={(loc) => {
                    setFormData(prev => ({
                      ...prev,
                      city: loc.city || loc.address || '',
                      state: loc.state || '',
                      latitude: loc.lat || null,
                      longitude: loc.lng || null
                    }));
                  }}
                />
              </div>
              <Input
                label="City"
                name="city"
                value={formData.city}
                onChange={handleChange}
                error={errors.city}
                required
                placeholder="e.g., Bangalore"
              />
              <Input
                label="State"
                name="state"
                value={formData.state}
                onChange={handleChange}
                placeholder="e.g., Karnataka"
              />
              {forRent && (
                <>
                  <Input
                    label="Price Per Day (₹)"
                    name="pricePerDay"
                    type="number"
                    value={formData.pricePerDay}
                    onChange={handleChange}
                    error={errors.pricePerDay}
                    required
                  />
                  <Input
                    label="Price Per KM (₹)"
                    name="pricePerKm"
                    type="number"
                    value={formData.pricePerKm}
                    onChange={handleChange}
                  />
                  <Input
                    label="Min Booking Days"
                    name="minBookingDays"
                    type="number"
                    value={formData.minBookingDays}
                    onChange={handleChange}
                    min="1"
                  />
                </>
              )}
            </div>

            {/* Service Types */}
            {forRent && (
            <>
            <SectionTitle>Service Types</SectionTitle>
            <div className="flex flex-wrap gap-3 mb-4">
              {[
                { value: 'rental', label: 'Multi-Day Rental' },
                { value: 'daily', label: 'Daily Rental' },
                { value: 'km-based', label: 'Pay Per KM' },
                { value: 'outstation', label: 'Outstation' },
                { value: 'local', label: 'Local Trips' }
              ].map((type) => (
                <label key={type.value} className="flex items-center space-x-2 text-sm cursor-pointer px-3 py-2 border rounded-lg hover:bg-gray-50">
                  <input
                    type="checkbox"
                    checked={formData.serviceTypes.includes(type.value)}
                    onChange={(e) => {
                      if (e.target.checked) {
                        setFormData(prev => ({ ...prev, serviceTypes: [...prev.serviceTypes, type.value] }));
                      } else {
                        setFormData(prev => ({ ...prev, serviceTypes: prev.serviceTypes.filter(t => t !== type.value) }));
                      }
                    }}
                    className="w-4 h-4 text-blue-600 rounded"
                  />
                  <span className="text-gray-700">{type.label}</span>
                </label>
              ))}
            </div>

            {hasOutstation && (
              <div className="grid grid-cols-2 md:grid-cols-3 gap-4 mb-4 p-3 bg-gray-50 rounded-lg">
                <Input
                  label="Outstation Price/KM (₹)"
                  name="outstationPricePerKm"
                  type="number"
                  value={formData.outstationPricePerKm}
                  onChange={handleChange}
                />
                <Input
                  label="Driver Bata/Day (₹)"
                  name="driverBataPerDay"
                  type="number"
                  value={formData.driverBataPerDay}
                  onChange={handleChange}
                />
              </div>
            )}
            </>
            )}

            {/* Sale details — shown when this vehicle is listed for sale */}
            {forSale && (
            <div className="mb-6">
              <SectionTitle>Sale Details</SectionTitle>
                <div className="grid grid-cols-2 md:grid-cols-3 gap-4 p-3 bg-green-50 rounded-lg">
                  <div>
                    <Input
                      label="Sale Price (₹)"
                      name="salePrice"
                      type="number"
                      value={formData.salePrice}
                      onChange={handleChange}
                      error={errors.salePrice}
                      required
                    />
                    {formData.salePrice && parseFloat(formData.salePrice) > 0 && (
                      <p className="mt-1 text-xs text-green-600 font-medium">
                        {numberToIndianWords(parseFloat(formData.salePrice))}
                      </p>
                    )}
                  </div>
                  <Select
                    label="Price Type"
                    name="salePriceType"
                    value={formData.salePriceType}
                    onChange={handleChange}
                  >
                    <option value="fixed">Fixed Price</option>
                    <option value="negotiable">Negotiable</option>
                    <option value="best-offer">Best Offer</option>
                    <option value="contact-for-price">Contact for Price</option>
                  </Select>

                  {/* Resale Details — shown to buyers */}
                  <div className="md:col-span-2 border-t border-gray-200 pt-4 mt-1">
                    <p className="text-sm font-semibold text-gray-800 mb-3">Resale Details <span className="font-normal text-gray-400">(shown to buyers)</span></p>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <Input label="Odometer / KM Driven" name="saleKmDriven" type="number" value={formData.saleKmDriven} onChange={handleChange} />
                      <Select label="No. of Owners" name="saleNoOfOwners" value={formData.saleNoOfOwners} onChange={handleChange}>
                        <option value="">Select</option>
                        {OWNER_OPTIONS.map((o) => (
                          <option key={o.value} value={o.value}>{o.label}</option>
                        ))}
                      </Select>
                      <Select label="Condition" name="saleCondition" value={formData.saleCondition} onChange={handleChange}>
                        <option value="">Select</option>
                        <option value="excellent">Excellent</option>
                        <option value="good">Good</option>
                        <option value="fair">Fair</option>
                        <option value="needs-work">Needs Work</option>
                      </Select>
                      <Input label="Registration Year" name="saleRegistrationYear" type="number" value={formData.saleRegistrationYear} onChange={handleChange} error={errors.saleRegistrationYear} />
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">Insurance Valid Till</label>
                        <input type="date" name="saleInsuranceValidTill" value={formData.saleInsuranceValidTill} onChange={handleChange}
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500" />
                      </div>
                      <div>
                        <Select label="Loan / Hypothecation" name="saleLoanStatus" value={formData.saleLoanStatus} onChange={handleChange}>
                          <option value="">Prefer not to say</option>
                          <option value="none">No loan taken</option>
                          <option value="closed">Loan closed — NOC available</option>
                          <option value="active">Loan running — NOC pending</option>
                        </Select>
                        <p className="text-[11px] text-gray-400 mt-1">Buyers cannot transfer the RC until the bank issues an NOC.</p>
                      </div>
                      <div className="flex items-center gap-4 pt-6">
                        <label className="flex items-center gap-2 cursor-pointer text-sm text-gray-700">
                          <input type="checkbox" checked={formData.saleAccidentFree} onChange={(e) => setFormData(prev => ({ ...prev, saleAccidentFree: e.target.checked }))} className="h-4 w-4 text-blue-600 rounded border-gray-300" />
                          Accident-free
                        </label>
                        <label className="flex items-center gap-2 cursor-pointer text-sm text-gray-700">
                          <input type="checkbox" checked={formData.saleRcAvailable} onChange={(e) => setFormData(prev => ({ ...prev, saleRcAvailable: e.target.checked }))} className="h-4 w-4 text-blue-600 rounded border-gray-300" />
                          RC available
                        </label>
                      </div>
                    </div>
                  </div>
                </div>
            </div>
            )}

            {/* Additional Details */}
            <SectionTitle>Additional Details</SectionTitle>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Features (comma separated)</label>
                <textarea
                  name="features"
                  value={formData.features}
                  onChange={handleChange}
                  rows="2"
                  className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm text-gray-900 bg-white focus:outline-none focus:ring-2 focus:ring-blue-500"
                  placeholder="GPS, AC, Bluetooth, Sunroof"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Description</label>
                <textarea
                  name="description"
                  value={formData.description}
                  onChange={handleChange}
                  rows="2"
                  className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm text-gray-900 bg-white focus:outline-none focus:ring-2 focus:ring-blue-500"
                  placeholder="Brief description of the vehicle..."
                />
              </div>
            </div>

            {/* Submit */}
            <div className="flex justify-end space-x-3 pt-4 border-t">
              <Button type="button" variant="outline" onClick={() => navigate('/cars')}>
                Cancel
              </Button>
              <Button type="submit" disabled={saving || uploadingImages}>
                {saving || uploadingImages ? (
                  <>
                    <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                    {uploadingImages ? 'Uploading...' : 'Saving...'}
                  </>
                ) : (
                  <>
                    <Save className="h-4 w-4 mr-2" />
                    Save Changes
                  </>
                )}
              </Button>
            </div>
          </CardContent>
        </Card>
      </form>

      {blurIndex !== null && imagePreviews[blurIndex] && (
        <PlateBlurModal
          file={imagePreviews[blurIndex].file}
          onCancel={() => setBlurIndex(null)}
          onApply={(f) => applyBlur(blurIndex, f)}
        />
      )}

      {blurExistingUrl && (
        <PlateBlurModal
          src={blurExistingUrl}
          name="edited-photo.jpg"
          onCancel={() => setBlurExistingUrl(null)}
          onApply={(f) => replaceExisting(blurExistingUrl, f)}
        />
      )}

      <ToastModal
        isOpen={toast.isOpen}
        onClose={() => setToast({ ...toast, isOpen: false })}
        title={toast.title}
        message={toast.message}
        type={toast.type}
      />

      <ConfirmModal
        isOpen={confirm.isOpen}
        onClose={() => setConfirm(prev => ({ ...prev, isOpen: false }))}
        onConfirm={confirm.onConfirm}
        title={confirm.title}
        message={confirm.message}
        type={confirm.type}
        confirmText={confirm.confirmText}
      />
    </div>
  );
};

export default EditCar;
